
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/auth-context";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import {
  BarChart3,
  Home,
  Plus,
  FileText,
  Settings,
  LogOut,
  Play,
  User,
  Plug,
  ChevronLeft,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";

interface DashboardSidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  isCreator: boolean;
  walletData: {
    balance: number;
    agentsPurchased: number;
    lastPurchase: string;
  };
  loadingWallet: boolean;
}

export function DashboardSidebar({
  activeTab,
  setActiveTab,
  isCreator,
  walletData,
  loadingWallet
}: DashboardSidebarProps) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const isNotInDashboard = location.pathname !== "/dashboard";

  // Main navigation items
  const navItems = [
    {
      href: "/dashboard",
      label: "Dashboard",
      icon: <Home className="mr-2 h-4 w-4" />,
    },
    {
      href: "/my-agents",
      label: "My Agents",
      icon: <FileText className="mr-2 h-4 w-4" />,
    },
    {
      href: "/run-agent",
      label: "Run Agents",
      icon: <Play className="mr-2 h-4 w-4" />,
    },
    {
      href: "/ai-connections",
      label: "AI Connections",
      icon: <Plug className="mr-2 h-4 w-4" />,
    },
    {
      href: "/profile",
      label: "Profile",
      icon: <User className="mr-2 h-4 w-4" />,
    },
    ...(isCreator
      ? [
          {
            href: "/create-agent",
            label: "Create Agent",
            icon: <Plus className="mr-2 h-4 w-4" />,
          },
          {
            href: "/dashboard?tab=analytics",
            label: "Analytics",
            icon: <BarChart3 className="mr-2 h-4 w-4" />,
            onClick: () => {
              if (location.pathname === "/dashboard") {
                setActiveTab("analytics");
              }
            }
          },
        ]
      : []),
  ];

  return (
    <aside className="hidden md:block border-r bg-gray-50/40 dark:bg-gray-950/40 w-64 p-6 pt-10">
      {isNotInDashboard && (
        <Link
          to="/dashboard"
          className={cn(
            buttonVariants({ variant: "outline" }),
            "w-full justify-start mb-4 text-primary"
          )}
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Link>
      )}

      <nav className="space-y-1">
        {navItems.map((item, i) => (
          <Link
            key={i}
            to={item.href}
            onClick={item.onClick}
            className={cn(
              buttonVariants({ variant: "ghost" }),
              "w-full justify-start mb-1",
              (location.pathname === item.href || 
               (item.href === "/dashboard?tab=analytics" && location.pathname === "/dashboard" && activeTab === "analytics"))
                ? "bg-muted hover:bg-muted font-medium"
                : ""
            )}
          >
            {item.icon}
            {item.label}
          </Link>
        ))}
      </nav>

      <Separator className="my-6" />

      <nav className="space-y-1">
        <Link
          to="/settings"
          className={cn(
            buttonVariants({ variant: "ghost" }),
            "w-full justify-start",
            location.pathname === "/settings"
              ? "bg-muted hover:bg-muted font-medium"
              : ""
          )}
        >
          <Settings className="mr-2 h-4 w-4" />
          Settings
        </Link>
        <button
          onClick={() => logout()}
          className={cn(
            buttonVariants({ variant: "ghost" }),
            "w-full justify-start text-red-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20"
          )}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </button>
      </nav>
    </aside>
  );
}
